<template>
  <f7-page
    :theme-dark="dark"
    id="settings"
  >

    <f7-navbar
      large
      back-link
      :theme-dark="dark"
      :title="t('settingsTitle')"
    />

    <f7-list>
      <f7-list-item
        v-if="lang"
        link="/settingslang"
        :title="t('settingsLang')"
        :footer="lang.name"
      >
        <FlagIcon
          slot="media"
          rounded
          :size="32"
          :code="lang.flag"
        />
      </f7-list-item>
      <f7-list-item
        link="/settingsmap"
        :title="t('settingsMap')"
        :footer="t('tiles' + tiles)"
      >
        <img
          slot="media"
          class="media-tile"
          :src="'img/map/' + setting('tiles') + '.png' "
        >
      </f7-list-item>
      <f7-list-item
        :title="t('settingsGps')"
        class="settings-slider"
      >
        <div slot="footer">
          <div>{{ satelliteFooter }}</div>
          <f7-range
            :min="0"
            :max="2"
            :value="setting('gps')"
            @range:changed="setGps"
          />
        </div>
        <FlatIcon
          slot="media"
          :size="32"
          name="satellite"
        />
      </f7-list-item>
      <f7-list-item
        :title="t('settingsCache')"
        class="settings-slider"
      >
        <div slot="footer">
          <div>{{ cacheFooter }}</div>
          <f7-range
            :min="1"
            :max="7"
            :value="setting('cache')"
            @range:changed="setCache"
          />
        </div>
        <FlatIcon
          slot="media"
          :size="32"
          name="photo"
        />
      </f7-list-item>
      <f7-list-item
        :title="t('settingsNosleep')"
        :footer="nosleepFooter"
      >
        <f7-toggle
          :checked="setting('nosleep')"
          @toggle:change="toggleNosleep"
        />
        <FlatIcon
          slot="media"
          :size="32"
          name="screen"
        />
      </f7-list-item>
      <f7-list-item
        :title="t('settingsKm')"
        :footer="kmFooter"
      >
        <f7-toggle
          :checked="setting('km')"
          @toggle:change="toggleKm"
        />
        <FlatIcon
          slot="media"
          :size="32"
          name="measuring-tape"
        />
      </f7-list-item>
      <f7-list-item
        :title="t('settingsSubtitles')"
        :footer="subtitlesFooter"
      >
        <f7-toggle
          :checked="setting('sub')"
          @toggle:change="toggleSubtitles"
        />
        <FlatIcon
          slot="media"
          :size="32"
          name="subtitles"
        />
      </f7-list-item>
      <f7-list-item
        :title="t('settingsDark')"
        :footer="darkFooter"
      >
        <f7-toggle
          :checked="setting('dark')"
          @toggle:change="toggleDark"
        />
        <FlatIcon
          slot="media"
          :size="32"
          name="dark"
        />
      </f7-list-item>
    </f7-list>

    <f7-list>
      <f7-list-item
        link="/settingsterms"
        :title="t('settingsTerms')"
        :footer="gtu"
      >
        <FlatIcon
          slot="media"
          :size="32"
          name="contract"
        />
      </f7-list-item>
    </f7-list>

  </f7-page>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { gpsDefaultOptions } from '@/services/geolocation'

export default {
  name: 'Settings',
  computed: {
    ...mapGetters({
      langCode: 'global/lang',
      langs: 'global/langs',
      setting: 'global/setting'
    }),
    lang () {
      return this.langs && this.langCode ? this.langs.find(l => l.code === this.langCode) : null
    },
    tiles () {
      return this.setting('tiles')
    },
    nosleep () {
      return this.setting('nosleep')
    },
    nosleepFooter () {
      return this.nosleep ? this.t('settingsNosleepEnabled') : this.t('settingsNosleepDisabled')
    },
    cacheFooter () {
      const txt = this.t('settingsCacheFooter')
      return txt.replace('XX', this.cache)
    },
    gps () {
      return this.setting('gps')
    },
    satelliteFooter () {
      let val
      if (this.gps === 2) val = 'Long (1s)'
      else if (this.gps === 1) val = 'Moyen (30s)'
      else val = 'Court (1 min)'
      return this.t('settingsGpsFooter') + val
    },
    kmFooter () {
      const text = this.t('settingsKmFooter')
      return this.setting('km') ? text + 'km' : text + 'mi'
    },
    darkFooter () {
      return this.dark ? this.t('enabled') : this.t('disabled')
    },
    subtitlesFooter () {
      return this.setting('sub') ? this.t('enabled') : this.t('disabled')
    },
    cache () {
      return this.setting('cache')
    },
    gtu () {
      return this.t('version') + ' ' + this.setting('gtu')
    }
  },
  watch: {
    nosleep (val) {
      if (val && window.plugins && window.plugins.insomnia) window.plugins.insomnia.keepAwake() // this.$nosleep.enable()
      else if (window.plugins && window.plugins.insomnia) window.plugins.insomnia.allowSleepAgain() // this.$nosleep.disable()
    },
    gps (val) {
      let opts = gpsDefaultOptions
      if (val !== 2) opts.enableHighAccuracy = false // allow low accuracy, all 1 pos / 30s
      if (val === 2) opts.maximumAge = 1000
      if (val === 1) opts.maximumAge = 30000
      if (val === 0) opts.maximumAge = 60000
      this.geoStart(opts)
    }
  },
  methods: {
    ...mapActions({
      setSetting: 'global/setSetting',
      geoStart: 'global/geoStart'
    }),
    toggleNosleep () {
      const nosleep = !this.nosleep
      this.setSetting({ nosleep })
    },
    toggleKm () {
      const km = !this.setting('km')
      this.setSetting({ km })
    },
    toggleDark () {
      const dark = !this.dark
      this.setSetting({ dark })
      if (dark) this.setSetting({ tiles: 'darkmatter' })
      else this.setSetting({ tiles: 'basic' })
    },
    toggleSubtitles () {
      const sub = !this.setting('sub')
      this.setSetting({ sub })
    },
    setGps (gps) {
      this.setSetting({ gps })
    },
    setCache (cache) {
      this.setSetting({ cache })
    }
  }
}
</script>

<style lang="scss">
.settings-slider {
  .item-title {
    width: 100%;
    overflow: visible !important;
  }
  .range-slider {
    margin: 8px 0;
  }
}
</style>

<docs>
```js
<Settings />
```
</docs>
